import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import './AdminPages.css';

const PageEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);

  useEffect(() => {
    fetchPageData();
  }, [slug]);

  const fetchPageData = async () => {
    try {
      const [pageRes, sectionsRes] = await Promise.all([
        api.get(`/admin/pages/${slug}`),
        api.get(`/admin/pages/${getPageId()}/sections`)
      ]);
      setPage(pageRes.data);
      setSections(sectionsRes.data || []);
    } catch (err) {
      setError('Failed to load page data');
    } finally {
      setLoading(false);
    }
  };

  const getPageId = () => {
    const pageMap = { home: 1, about: 2, contact: 3, 'clients-services': 4, blog: 5 };
    return pageMap[slug] || 1;
  };

  const handlePageUpdate = async () => {
    try {
      await api.put(`/admin/pages/${getPageId()}`, page);
      setSuccess('Page updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update page');
    }
  };

  const handleAddSection = async (sectionData) => {
    try {
      await api.post(`/admin/pages/${getPageId()}/sections`, sectionData);
      setSuccess('Section added successfully!');
      setShowAddSection(false);
      fetchPageData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add section');
    }
  };

  const handleUpdateSection = async (sectionId, sectionData) => {
    try {
      await api.put(`/admin/sections/${sectionId}`, sectionData);
      setSuccess('Section updated successfully!');
      setEditingSection(null);
      fetchPageData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update section');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    try {
      await api.delete(`/admin/sections/${sectionId}`);
      setSuccess('Section deleted successfully!');
      fetchPageData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete section');
    }
  };

  const handlePublishSection = async (sectionId) => {
    try {
      await api.post(`/admin/sections/${sectionId}/publish`);
      setSuccess('Section published successfully!');
      fetchPageData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to publish section');
    }
  };

  const handlePublishPage = async () => {
    try {
      await api.post(`/admin/pages/${getPageId()}/publish`);
      setSuccess('Page published successfully!');
      fetchPageData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to publish page');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Edit {slug.charAt(0).toUpperCase() + slug.slice(1)} Page</h1>
          <div className="button-group">
            <button onClick={() => navigate('/admin')} className="btn btn-secondary">
              Back to Dashboard
            </button>
            <button onClick={() => navigate(`/admin/preview/${slug}`)} className="btn btn-secondary">
              Preview
            </button>
            <button onClick={handlePublishPage} className="btn btn-success">
              Publish Page
            </button>
          </div>
        </div>

        {success && <div className="success">{success}</div>}
        {error && <div className="error">{error}</div>}

        {/* Page Meta Information */}
        <div className="card">
          <h2>Page Settings</h2>
          <div className="form-group">
            <label>Page Title</label>
            <input
              type="text"
              value={page?.title || ''}
              onChange={(e) => setPage({ ...page, title: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Meta Description (for SEO)</label>
            <textarea
              value={page?.meta_description || ''}
              onChange={(e) => setPage({ ...page, meta_description: e.target.value })}
              rows="3"
              placeholder="Brief description for search engines (150-160 characters)"
            />
          </div>
          <button onClick={handlePageUpdate} className="btn btn-primary">
            Save Page Settings
          </button>
        </div>

        {/* Page Sections */}
        <div className="card" style={{ marginTop: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2>Page Sections</h2>
            <button onClick={() => setShowAddSection(true)} className="btn btn-primary">
              + Add Section
            </button>
          </div>

          {sections.length === 0 && (
            <p style={{ textAlign: 'center', color: 'var(--secondary-color)' }}>
              No sections yet. Add your first section to get started!
            </p>
          )}

          {sections.map((section) => (
            <div key={section.id} className="section-item" style={{
              border: '1px solid #ddd',
              padding: '20px',
              marginBottom: '15px',
              borderRadius: '8px',
              backgroundColor: section.is_published ? '#f8f9fa' : '#fff3cd'
            }}>
              {editingSection === section.id ? (
                <SectionForm
                  section={section}
                  onSave={(data) => handleUpdateSection(section.id, data)}
                  onCancel={() => setEditingSection(null)}
                />
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h3>{section.title || 'Untitled Section'}</h3>
                      <p style={{ color: 'var(--secondary-color)', fontSize: '0.9rem' }}>
                        Type: {section.section_type} | Order: {section.order_index}
                      </p>
                      {section.content && (
                        <p style={{ marginTop: '10px' }}>{section.content.substring(0, 200)}...</p>
                      )}
                      {section.image_url && (
                        <img
                          src={section.image_url}
                          alt={section.title}
                          style={{ maxWidth: '200px', marginTop: '10px', borderRadius: '4px' }}
                        />
                      )}
                    </div>
                    <div className="action-buttons">
                      <button onClick={() => setEditingSection(section.id)} className="btn btn-secondary">
                        Edit
                      </button>
                      {!section.is_published && (
                        <button onClick={() => handlePublishSection(section.id)} className="btn btn-success">
                          Publish
                        </button>
                      )}
                      <button onClick={() => handleDeleteSection(section.id)} className="btn btn-danger">
                        Delete
                      </button>
                    </div>
                  </div>
                  {!section.is_published && (
                    <span className="status-badge status-draft" style={{ marginTop: '10px', display: 'inline-block' }}>
                      Draft
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add Section Modal */}
        {showAddSection && (
          <div className="modal-overlay" onClick={() => setShowAddSection(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Add New Section</h2>
              <SectionForm
                onSave={handleAddSection}
                onCancel={() => setShowAddSection(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SectionForm = ({ section = {}, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    section_type: section.section_type || 'content',
    title: section.title || '',
    content: section.content || '',
    image_url: section.image_url || '',
    order_index: section.order_index || 0,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Section Type</label>
        <select
          value={formData.section_type}
          onChange={(e) => setFormData({ ...formData, section_type: e.target.value })}
        >
          <option value="hero">Hero Section</option>
          <option value="content">Content Section</option>
          <option value="grid">Grid Section</option>
          <option value="testimonial">Testimonial</option>
          <option value="cta">Call to Action</option>
        </select>
      </div>

      <div className="form-group">
        <label>Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>Content</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          rows="6"
        />
      </div>

      <div className="form-group">
        <label>Image URL</label>
        <input
          type="text"
          value={formData.image_url}
          onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
          placeholder="/uploads/image.jpg or https://..."
        />
        <small style={{ color: 'var(--secondary-color)' }}>
          Upload images in Media Library and copy the URL here
        </small>
      </div>

      <div className="form-group">
        <label>Order Index</label>
        <input
          type="number"
          value={formData.order_index}
          onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) })}
          min="0"
        />
      </div>

      <div className="button-group">
        <button type="submit" className="btn btn-primary">
          Save Section
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default PageEditor;
